import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import appConfig from '../config/appConfig';

const MAX_SESSION_DAYS_REMEMBER = 30;
const MAX_SESSION_DAYS_DEFAULT = 1;

// Helper: Kiểm tra xem session đã quá hạn chưa
const isSessionExpired = (loginTime, rememberMe) => {
  if (!loginTime) return true;
  const maxDays = rememberMe ? MAX_SESSION_DAYS_REMEMBER : MAX_SESSION_DAYS_DEFAULT;
  const daysSinceLogin = (Date.now() - loginTime) / (1000 * 60 * 60 * 24);
  return daysSinceLogin > maxDays;
};

// Flag để tránh multiple logout calls
let isLoggingOut = false;

/**
 * GỌI /auth/refresh BẰNG AXIOS TRẦN, KHÔNG QUA `api`.
 *
 * Sự cố 17/09/2026: `refreshAccessToken` gọi `api.post('/auth/refresh')`, mà chính
 * `api` lại có interceptor "gặp 401 thì đi refresh". Khi refresh token hết hạn,
 * server trả 401 cho ĐÚNG lời gọi refresh đó → interceptor lại gọi refresh →
 * đệ quy vô hạn. Đo được 1.179 request /auth/refresh trong 60 giây (≈110 req/s)
 * từ một trình duyệt; toàn bộ hạn mức 1.200 request/5 phút của người đó cháy sạch
 * nên mọi API sau đó trả 429. `forceLogout` trong nhánh catch không bao giờ chạy
 * vì không lần gọi nào kịp settle.
 *
 * Axios trần không mang interceptor ⇒ 401 dừng ngay tại đây. `withCredentials`
 * vẫn cần để trình duyệt gửi cookie httpOnly `refreshToken`.
 * (Mobile `mobile/src/api/client.js` vốn đã làm đúng cách này.)
 */
const rawRefreshCall = () =>
  // Body PHẢI là `{}`, KHÔNG được là `null`. Khi Content-Type đã là application/json,
  // axios chạy `JSON.stringify(data)` ⇒ `null` biến thành 4 ký tự `null` gửi lên
  // server, và body-parser ở chế độ strict chỉ nhận object/array ở cấp cao nhất nên
  // ném "Unexpected token n in JSON at position 0" ⇒ 400 TRƯỚC KHI vào controller.
  // Hậu quả (đo trên prod 17-18/09): mọi lượt làm mới token đều 400 bất kể cookie còn
  // hạn hay không — người dùng bị đá ra màn đăng nhập thay vì được gia hạn phiên.
  axios.post(`${appConfig.getApiUrl()}/auth/refresh`, {}, {
    withCredentials: true,
    timeout: 30000,
  });

// Gộp nhiều lời gọi refresh cùng lúc thành MỘT. Nhiều request 401 song song
// (trang nào cũng gọi 4-5 API lúc mở) nếu không gộp sẽ bắn ngần ấy lần refresh.
let refreshPromise = null;

// Persistent flag to prevent redirect loops across page reloads
const LOGOUT_REDIRECT_KEY = 'auth-logout-redirecting';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isValidating: false, // Flag khi đang validate session
      loginTime: null, // Thời điểm đăng nhập
      rememberMe: false, // Ghi nhớ đăng nhập
      pagePermissions: [], // Quyền truy cập trang của user

      // Force logout - dùng khi detect thay đổi security
      forceLogout: (reason = 'Phiên đăng nhập đã hết hạn') => {
        // Prevent multiple logout calls (in-memory flag)
        if (isLoggingOut) {
          console.log('[Auth] Already logging out, skipping...');
          return;
        }

        // Prevent redirect loops across page reloads (persistent flag)
        try {
          const redirectFlag = sessionStorage.getItem(LOGOUT_REDIRECT_KEY);
          if (redirectFlag) {
            const elapsed = Date.now() - parseInt(redirectFlag, 10);
            if (elapsed < 5000) {
              console.warn('[Auth] Redirect loop detected — skipping forceLogout');
              return;
            }
          }
        } catch (e) { /* sessionStorage unavailable */ }

        isLoggingOut = true;
        
        console.warn('[Auth] Force logout:', reason);
        
        // Clear state FIRST
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isValidating: false,
          loginTime: null,
          rememberMe: false,
          pagePermissions: [],
        });
        
        // Clear storage (but keep redirect flag)
        localStorage.removeItem('auth-storage');
        
        // Clear cookies nếu có
        document.cookie.split(";").forEach((c) => {
          document.cookie = c
            .replace(/^ +/, "")
            .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        // Check if safe to redirect
        const isChromeError = () => {
          try {
            return window.location.href.includes('chrome-error://')
              || window.location.protocol === 'chrome-error:';
          } catch (e) {
            return true; // Assume unsafe if we can't check
          }
        };

        // If already on login page, no need to redirect
        if (window.location.pathname === '/login') {
          console.log('[Auth] Already on login page, skipping redirect');
          isLoggingOut = false;
          return;
        }

        // Redirect to login — retry if offline to avoid chrome-error:// page
        const redirectUrl = '/login?reason=' + encodeURIComponent(reason);
        console.log('[Auth] Redirecting to:', redirectUrl);
        
        const doRedirect = () => {
          if (isChromeError()) {
            console.warn('[Auth] On chrome-error page — cannot redirect safely');
            isLoggingOut = false;
            return;
          }

          if (navigator.onLine) {
            try { sessionStorage.setItem(LOGOUT_REDIRECT_KEY, Date.now().toString()); } catch (e) {}
            isLoggingOut = false;
            window.location.replace(redirectUrl);
          } else {
            console.warn('[Auth] Offline — waiting for network before redirect');
            const onOnline = () => {
              window.removeEventListener('online', onOnline);
              if (isChromeError()) {
                isLoggingOut = false;
                return;
              }
              try { sessionStorage.setItem(LOGOUT_REDIRECT_KEY, Date.now().toString()); } catch (e) {}
              isLoggingOut = false;
              window.location.replace(redirectUrl);
            };
            window.addEventListener('online', onOnline);
            setTimeout(() => {
              window.removeEventListener('online', onOnline);
              isLoggingOut = false;
              if (navigator.onLine && !isChromeError()) {
                try { sessionStorage.setItem(LOGOUT_REDIRECT_KEY, Date.now().toString()); } catch (e) {}
                window.location.replace(redirectUrl);
              }
            }, 10000);
          }
        };

        setTimeout(doRedirect, 100);
      },

      // Check session validity on load (local check only)
      checkSession: () => {
        const { loginTime, isAuthenticated, rememberMe } = get();
        
        // Nếu chưa có loginTime nhưng đã authenticated, có thể là mới login - không logout
        if (isAuthenticated && !loginTime) {
          console.warn('[Auth] No loginTime but authenticated - assuming fresh login');
          // Set loginTime ngay để tránh check lại
          set({ loginTime: Date.now() });
          return true;
        }
        
        if (isAuthenticated && isSessionExpired(loginTime, rememberMe)) {
          const maxDays = rememberMe ? MAX_SESSION_DAYS_REMEMBER : MAX_SESSION_DAYS_DEFAULT;
          get().forceLogout(`Phiên đăng nhập đã hết hạn (quá ${maxDays} ngày). Vui lòng đăng nhập lại`);
          return false;
        }
        
        return true;
      },

      // Validate session với server - gọi khi app load
      validateSession: async () => {
        const { isAuthenticated, accessToken, isValidating } = get();
        
        // Nếu không authenticated hoặc đang validate, skip
        if (!isAuthenticated || !accessToken || isValidating) {
          console.log('[Auth] Skip validation - not authenticated or already validating');
          return !isAuthenticated; // Return true if not authenticated (no need to validate)
        }
        
        console.log('[Auth] Validating session with server...');
        set({ isValidating: true });
        
        try {
          // Check session expiry first
          if (!get().checkSession()) {
            set({ isValidating: false });
            return false;
          }
          
          // Call /auth/me to validate token
          const response = await api.get('/auth/me');
          
          if (response.data.success) {
            console.log('[Auth] Session valid');
            // Cookie cho phép trình duyệt tải ảnh trong /uploads (ảnh chấm công,
            // khuôn mặt, avatar). Thẻ <img> không gắn được header Authorization nên
            // không có cookie này là mọi ảnh 401. Gọi mỗi lần khôi phục phiên để
            // phiên đăng nhập từ TRƯỚC khi bật cổng ảnh cũng dùng được ngay.
            api.post('/auth/media-cookie').catch(() => {});
            set({ 
              user: response.data.data,
              isValidating: false 
            });
            return true;
          } else {
            console.warn('[Auth] Session invalid - server returned failure');
            get().forceLogout('Phiên đăng nhập không hợp lệ');
            return false;
          }
        } catch (error) {
          console.error('[Auth] Validation error:', error.response?.status, error.message);
          set({ isValidating: false });
          
          // Nếu lỗi 401, force logout
          if (error.response?.status === 401) {
            const message = error.response?.data?.message || 'Phiên đăng nhập đã hết hạn';
            get().forceLogout(message);
            return false;
          }
          
          // Nếu lỗi network, cho phép tiếp tục (offline mode)
          if (!error.response) {
            console.warn('[Auth] Network error - allowing offline mode');
            return true;
          }
          
          return false;
        }
      },

      // Update user info (khi edit profile)
      updateUser: (updatedFields) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updatedFields } });
        }
      },

      // Đổi mật khẩu — backend cấp lại accessToken mới (token_version mới) để
      // phiên tiếp tục liền mạch, đồng thời bỏ cờ buộc đổi mật khẩu lần đầu.
      changePassword: async (currentPassword, newPassword) => {
        try {
          const response = await api.post('/auth/change-password', { currentPassword, newPassword });
          const newAccessToken = response.data?.data?.accessToken;
          const { user } = get();
          set({
            ...(newAccessToken ? { accessToken: newAccessToken } : {}),
            user: user ? { ...user, must_change_password: false } : user,
          });
          return { success: true };
        } catch (error) {
          const data = error.response?.data;
          const message = data?.message
            || (Array.isArray(data?.errors) ? data.errors.join('. ') : null)
            || 'Lỗi đổi mật khẩu';
          return { success: false, message };
        }
      },

      // Login
      login: async (email, password, rememberMe = false) => {
        set({ isLoading: true, pagePermissions: [] }); // Clear old permissions
        try {
          const response = await api.post('/auth/login', { email, password, remember_me: rememberMe });
          // refreshToken không có trong response body — chỉ được set qua httpOnly cookie
          const { employee, accessToken } = response.data.data;

          console.log('[Auth] Login successful, tokens received:', {
            hasAccessToken: !!accessToken,
            employee: employee?.employee_id,
            rememberMe,
          });

          set({
            user: employee,
            accessToken,
            refreshToken: null, // refreshToken là httpOnly cookie, không lưu trong state
            isAuthenticated: true,
            isLoading: false,
            loginTime: Date.now(), // Lưu thời điểm login
            rememberMe, // Lưu trạng thái ghi nhớ
            pagePermissions: [], // Reset page permissions, will be loaded by Layout
          });

          console.log('[Auth] State updated after login');
          // Clear redirect loop protection flag on successful login
          try { sessionStorage.removeItem(LOGOUT_REDIRECT_KEY); } catch (e) {}
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return {
            success: false,
            message: error.response?.data?.message || 'Đăng nhập thất bại',
          };
        }
      },

      // Logout
      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear storage
          localStorage.removeItem('auth-storage');
          
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            loginTime: null,
            rememberMe: false,
            pagePermissions: [], // Clear page permissions
          });
        }
      },

      // Refresh token
      // Backend trả refreshToken qua httpOnly cookie (không có trong response body).
      // Trình duyệt tự gửi cookie khi POST /auth/refresh → KHÔNG cần lưu refreshToken trong state.
      refreshAccessToken: async () => {
        console.log('[Auth] Attempting to refresh token via httpOnly cookie...');

        // Kiểm tra session expiry
        if (!get().checkSession()) {
          return false;
        }

        // Đã có một lần refresh đang bay → dùng chung kết quả, không bắn thêm.
        if (refreshPromise) return refreshPromise;

        refreshPromise = (async () => {
          try {
            console.log('[Auth] Calling /auth/refresh API');
            // Không gửi refreshToken trong body — cookie httpOnly được gửi tự động bởi browser
            const response = await rawRefreshCall();
            const { accessToken: newAccessToken } = response.data.data;

            console.log('[Auth] Token refreshed successfully');
            set({ accessToken: newAccessToken });
            return true;
          } catch (error) {
            console.error('[Auth] Refresh token error:', error.response?.data || error.message);
          
            // Network error — do NOT force logout, allow offline continuation
            if (!error.response) {
              console.warn('[Auth] Network error during refresh — staying logged in');
              return false;
            }

            // 429 = bị giới hạn tần suất, KHÔNG phải phiên hết hạn. Đá người dùng ra
            // màn đăng nhập lúc này là oan: token vẫn còn giá trị, chỉ là gọi quá dày.
            if (error.response.status === 429) {
              console.warn('[Auth] Bị giới hạn tần suất khi refresh — giữ nguyên phiên');
              return false;
            }

            // Nếu là TOKEN_VERSION_MISMATCH, force logout với message cụ thể
            if (error.response?.data?.code === 'TOKEN_VERSION_MISMATCH') {
              get().forceLogout(error.response.data.message);
            } else {
              get().forceLogout('Phiên đăng nhập đã hết hạn');
            }

            return false;
          }
        })();

        try {
          return await refreshPromise;
        } finally {
          refreshPromise = null;
        }
      },

      // Get current user - luôn validate khi load form
      getCurrentUser: async () => {
        try {
          // Kiểm tra session trước
          if (!get().checkSession()) {
            return null;
          }

          const response = await api.get('/auth/me');
          set({ user: response.data.data });
          return response.data.data;
        } catch (error) {
          console.error('Get current user error:', error);
          
          // Nếu lỗi 401, force logout
          if (error.response?.status === 401) {
            if (error.response?.data?.code === 'TOKEN_VERSION_MISMATCH') {
              get().forceLogout(error.response.data.message);
            } else {
              get().forceLogout('Phiên đăng nhập không hợp lệ');
            }
          }
          
          return null;
        }
      },

      // Load page permissions for current user
      loadPagePermissions: async () => {
        try {
          const response = await api.get('/page-permissions/my-pages');
          if (response.data.success) {
            set({ pagePermissions: response.data.data });
            return response.data.data;
          }
          return [];
        } catch (error) {
          console.error('Load page permissions error:', error);
          return [];
        }
      },

      // Check if user has access to a page
      hasPageAccess: (pagePath) => {
        const { pagePermissions } = get();
        
        // If no page permissions loaded, deny access (wait for load)
        if (!pagePermissions || pagePermissions.length === 0) {
          // Special pages that are always accessible
          const alwaysAccessible = ['/profile', '/login'];
          return alwaysAccessible.includes(pagePath);
        }

        // Flatten pages with children
        const flattenPages = (pages) => {
          let result = [];
          for (const page of pages) {
            result.push(page);
            if (page.children && page.children.length > 0) {
              result = result.concat(flattenPages(page.children));
            }
          }
          return result;
        };

        const allPages = flattenPages(pagePermissions);
        const page = allPages.find(p => p.page_path === pagePath);

        if (page) {
          return page.permissions?.can_view !== false;
        }

        // If page not in list, deny access
        return false;
      },

      // Get accessible pages for navigation
      getAccessiblePages: () => {
        return get().pagePermissions;
      },

      // Get sub-page permissions for a parent page
      // Returns { [subPageCode]: { can_view, can_create, can_edit, can_delete } }
      getSubPagePermissions: (parentPath) => {
        const { pagePermissions } = get();
        if (!pagePermissions || pagePermissions.length === 0) return {};

        // Find parent page and its children
        const parent = pagePermissions.find(p => p.page_path === parentPath);
        if (!parent || !parent.children) return {};

        const result = {};
        for (const child of parent.children) {
          result[child.page_code] = child.permissions || { can_view: false, can_create: false, can_edit: false, can_delete: false };
        }
        return result;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        loginTime: state.loginTime, // Lưu loginTime để kiểm tra expiry
        rememberMe: state.rememberMe, // Ghi nhớ đăng nhập
        // pagePermissions: KHÔNG persist - luôn load mới từ API
      }),
    }
  )
);
